import React, { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, ChevronDown, Gift, PersonStanding, ToyBrick, Baby, Car, Dices, Volleyball, Dog,
  LayoutGrid, Bot, Gamepad2, Scissors, BookOpen, Puzzle, Brain, Palette, Cake, ShoppingBag, Flame,
  ShoppingBasket, PenLine, CreditCard, Package, SlidersHorizontal, X,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TrustStrip from "../components/TrustStrip";
import ProductCard from "../components/ProductCard";
import FilterSidebar from "../components/FilterSidebar";
import GiftFinderQuiz from "../components/GiftFinderQuiz";
import { categoryPages } from "../mockCategories";
import { useStore } from "../context/StoreContext";
import { fetchProducts } from "../api";

const NAVY = "#1B1F5C";
const chipIcons = {
  PersonStanding, ToyBrick, Baby, Car, Dices, Volleyball, Dog, LayoutGrid, Bot, Gamepad2, Scissors,
  BookOpen, Puzzle, Brain, Palette, Cake, ShoppingBag, Flame, ShoppingBasket, PenLine, CreditCard, Package, Gift,
};

const EMPTY_FILTERS = { ages: [], prices: [], brands: [], rating: null, genders: [], occasions: [], min: "", max: "", brandSearch: "" };

const PAGE_SIZE = 12;

// Centralized subcategory → product matcher (products have no subcategory field in DB)
const SUBCATEGORY_MATCHERS = {
  "Soft Toys": /soft toy|plush|teddy|cuddly/i,
  "Building Blocks": /block|building|lego|magnetic|technic|construction/i,
  "Board Games": /board game|strategy|guess who|monopoly|game night|cluedo|scrabble|jenga|battleship|puzzle master/i,
  "Puzzles": /puzzle|jigsaw/i,
  "Remote Control": /\brc\b|remote|drone|monster truck/i,
  "Action Figures": /action figure|figures|collectible/i,
  "Ride-Ons": /ride-on|ride on|tricycle|swing car|scooter|slide/i,
  "Outdoor Play": /outdoor|cricket|football|tent|slide|pogo|hopping/i,
  "STEM Toys": /stem|robot|robo|circuit|gear|coding/i,
  "Art & Craft": /art|craft|crayon|paint|jewellery|hobby/i,
  "Musical Toys": /music|xylophone|tambourine|maracas|piano|drum/i,
  "Books & Flashcards": /book|flash card|flashcard/i,
  "Science Kits": /science|experiment|lab|solar/i,
  "Dolls & Dollhouses": /doll|barbie/i,
  "Baby & Toddler": /baby|rattle|teeth|cradle|walker|activity mat|sensory|stacking|sorter/i,
};

const matchesSubcategory = (p, label) => {
  if (!label || label === "All Categories" || label === "All") return true;
  if (label === "Gift Cards") return p.giftCard === true;
  if (/birthday/i.test(label)) return p.occasion ? /birthday/i.test(p.occasion) : false;
  if (/return gift/i.test(label)) return p.occasion ? /return/i.test(p.occasion) : false;
  if (/festive/i.test(label)) return p.occasion ? /festive|diwali|christmas/i.test(p.occasion) : false;
  const hay = `${p.name} ${p.description || ""} ${p.subcategory || ""}`;
  const re = SUBCATEGORY_MATCHERS[label];
  if (re) return re.test(hay) || (p.subcategory && p.subcategory.toLowerCase() === label.toLowerCase());
  const words = label.replace(/[^a-z0-9\s]/gi, "").split(/\s+/).filter((w) => w.length > 2);
  return words.some((w) => new RegExp(w, "i").test(hay));
};

const CategoryPage = ({ slug }) => {
  const config = categoryPages[slug];
  const { wishlist, cart, toggleWishlist, addToCart } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    ...EMPTY_FILTERS,
    ages: searchParams.get("age") ? searchParams.get("age").split("|") : [],
    brands: searchParams.get("brand") ? searchParams.get("brand").split("|") : [],
    rating: searchParams.get("rating") ? Number(searchParams.get("rating")) : null,
  }));
  const [selectedCategory, setSelectedCategory] = useState(() => searchParams.get("sub") || "All Categories");
  const [sort, setSort] = useState(() => searchParams.get("sort") || "Popularity");
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Keep URL in sync with state (so Back restores context)
  useEffect(() => {
    const p = {};
    if (filters.ages?.length) p.age = filters.ages.join("|");
    if (filters.brands?.length) p.brand = filters.brands.join("|");
    if (filters.rating) p.rating = String(filters.rating);
    if (selectedCategory && selectedCategory !== "All Categories") p.sub = selectedCategory;
    if (sort !== "Popularity") p.sort = sort;
    if (page > 1) p.page = String(page);
    const next = new URLSearchParams(p).toString();
    if (next !== searchParams.toString()) setSearchParams(p, { replace: true });
  }, [filters, selectedCategory, sort, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply external navigation (header dropdown links) to state
  useEffect(() => {
    const sub = searchParams.get("sub") || "All Categories";
    const age = searchParams.get("age");
    setSelectedCategory((cur) => (cur === sub ? cur : sub));
    if (age) {
      const ages = age.split("|");
      setFilters((f) => (JSON.stringify(f.ages) === JSON.stringify(ages) ? f : { ...f, ages }));
    }
  }, [searchParams]);

  // Reset to page 1 when filters materially change
  useEffect(() => {
    setPage(1);
  }, [filters.ages, filters.brands, filters.rating, filters.genders, filters.occasions, selectedCategory, sort]);

  // Load products from the backend (shop-by-age shows all categories)
  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = slug === "shop-by-age" ? {} : { category: slug };
    fetchProducts(params)
      .then((data) => active && setProducts(data))
      .catch(() => active && setProducts(config.products || []))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [slug, config]);

  const filtered = useMemo(() => {
    let list = [...products];
    list = list.filter((p) => matchesSubcategory(p, selectedCategory));
    if (filters.ages?.length) list = list.filter((p) => filters.ages.includes(p.age));
    if (filters.brands?.length) list = list.filter((p) => filters.brands.includes(p.brand));
    if (filters.rating) list = list.filter((p) => p.rating >= filters.rating);
    if (filters.genders?.length) list = list.filter((p) => filters.genders.includes(p.gender));
    if (filters.occasions?.length) list = list.filter((p) => filters.occasions.includes(p.occasion));
    if (sort === "Rating") list.sort((a, b) => b.rating - a.rating);
    else list.sort((a, b) => b.reviews - a.reviews);
    return list;
  }, [products, filters, sort, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1);

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedCategory("All Categories");
    setSort("Popularity");
    setPage(1);
  };

  return (
    <div className="bg-white min-h-screen">
      <Header cartCount={cart.length} wishlistCount={wishlist.length} />
      <main>
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 pt-4 text-left">
          <p className="text-[12px] font-bold" style={{ color: NAVY }}>
            <Link to="/" className="hover:text-[#35C3C1] transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">&gt;</span>
            <span>{config.breadcrumb}</span>
          </p>
        </div>

        {/* Hero banner */}
        <section className="max-w-7xl mx-auto px-4 mt-3">
          <div className="relative rounded-3xl overflow-hidden" style={{ backgroundColor: "#FBF3E4" }}>
            {/* doodle stars */}
            <svg className="absolute top-6 left-6 w-6 h-6 text-[#35C3C1] animate-float" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
            <svg className="absolute bottom-8 left-10 w-7 h-7 text-[#F0426A] animate-float-delay" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
            <svg className="absolute top-8 right-8 w-6 h-6 text-[#F79433] animate-float hidden md:block" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>

            <div className="grid md:grid-cols-2 items-center">
              <div className="p-7 md:p-10 lg:p-12 text-left relative z-10">
                <h1 className="font-display font-extrabold text-3xl md:text-4xl lg:text-[44px] leading-tight">
                  {config.heroTitle.map((part, i) => (
                    <span key={i} className={part.block ? "block" : ""} style={{ color: part.c }}>{part.t}</span>
                  ))}
                </h1>
                <p className="mt-3 text-[15px] md:text-base font-extrabold" style={{ color: NAVY }}>{config.heroSub}</p>
                <p className="mt-2 text-[13.5px] font-semibold text-[#5A5A75] whitespace-pre-line leading-relaxed">{config.heroDesc}</p>
                {config.heroButtons && (
                  <div className="mt-6 flex flex-wrap gap-3">
                    {config.heroButtons.map((b) =>
                      b.style === "solid" ? (
                        <button key={b.label} className="px-6 h-11 rounded-full text-white text-[12px] font-extrabold tracking-widest shadow-md hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: NAVY }}>
                          {b.label}
                        </button>
                      ) : (
                        <button
                          key={b.label}
                          onClick={() => setQuizOpen(true)}
                          className="px-6 h-11 rounded-full border-2 border-[#35C3C1] text-[#1B1F5C] bg-white/60 text-[12px] font-extrabold tracking-widest inline-flex items-center gap-2 hover:bg-[#35C3C1] hover:text-white transition-colors"
                        >
                          {b.label} <Gift size={15} />
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
              <div className="relative h-[220px] md:h-[300px] lg:h-[320px]">
                <img
                  src={config.heroImage}
                  alt={config.title}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  style={{ maskImage: "linear-gradient(to right, transparent 0%, black 18%)", WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 18%)" }}
                />
                {config.heroBadge && (
                  <div className="absolute top-4 right-4 md:top-6 md:right-6 h-28 w-28 md:h-32 md:w-32 rounded-full bg-[#7C4DC4] text-white flex items-center justify-center p-4 text-center shadow-xl rotate-3">
                    <p className="text-[10.5px] md:text-[11.5px] font-extrabold leading-snug">{config.heroBadge}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Category chips */}
        <section className="max-w-7xl mx-auto px-4 mt-8">
          <div className="flex gap-5 md:gap-8 overflow-x-auto no-scrollbar justify-start lg:justify-center pb-2">
            {config.chips.map((chip) => {
              const Icon = chipIcons[chip.icon] || LayoutGrid;
              const active = selectedCategory === chip.label;
              return (
                <button
                  key={chip.label}
                  onClick={() => setSelectedCategory(active ? "All Categories" : chip.label)}
                  data-testid={`chip-${chip.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex flex-col items-center shrink-0 group w-[86px]"
                >
                  <span
                    className={`h-16 w-16 md:h-[72px] md:w-[72px] rounded-full bg-white border shadow-sm flex items-center justify-center group-hover:shadow-md group-hover:-translate-y-0.5 transition-transform duration-200 ${active ? "border-[#35C3C1] border-2 ring-2 ring-[#35C3C1]/20" : "border-gray-200 group-hover:border-[#35C3C1]"}`}
                  >
                    <Icon size={26} style={{ color: active ? "#35C3C1" : NAVY }} strokeWidth={1.6} />
                  </span>
                  <span className="mt-2 text-[11.5px] font-extrabold leading-tight text-center" style={{ color: active ? "#35C3C1" : NAVY }}>{chip.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Main: sidebar + grid */}
        <section className="max-w-7xl mx-auto px-4 mt-6 grid lg:grid-cols-[250px_1fr] gap-6">
          <div className="hidden lg:block">
            <FilterSidebar config={config.sidebar} filters={filters} setFilters={setFilters} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 text-left">
              <div>
                <h2 className="font-display font-extrabold text-2xl" style={{ color: NAVY }}>{config.title}</h2>
                <p className="text-[12px] font-semibold text-gray-500 mt-0.5" data-testid="product-count">
                  {filtered.length === 0
                    ? "0 products"
                    : `Showing ${(safePage - 1) * PAGE_SIZE + 1} – ${Math.min(safePage * PAGE_SIZE, filtered.length)} of ${filtered.length} products`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMobileFilters(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-gray-200 text-[12px] font-extrabold"
                  style={{ color: NAVY }}
                >
                  <SlidersHorizontal size={14} /> Filters
                </button>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="appearance-none h-9 pl-4 pr-9 rounded-full border border-gray-200 bg-white text-[12px] font-extrabold outline-none cursor-pointer focus:border-[#35C3C1]"
                    style={{ color: NAVY }}
                  >
                    {["Popularity", "Rating"].map((s) => (
                      <option key={s} value={s}>{`Sort by: ${s}`}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: NAVY }} />
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 content-start">
              {loading
                ? [...Array(8)].map((_, i) => <div key={i} className="rounded-2xl bg-gray-100 h-72 animate-pulse" />)
                : paged.map((p) => (
                    <ProductCard key={p.id} product={p} badgeColor={config.badgeColor} wishlist={wishlist} toggleWishlist={toggleWishlist} addToCart={addToCart} />
                  ))}
            </div>
            {!loading && filtered.length === 0 && (
              <div className="py-16 text-center" data-testid="empty-results">
                <p className="text-sm font-semibold text-gray-400">No products match the selected filters.</p>
                <button
                  onClick={clearAllFilters}
                  data-testid="clear-filters-btn"
                  className="mt-4 px-6 h-10 rounded-full border-2 border-[#35C3C1] text-[11.5px] font-extrabold tracking-widest text-[#1B1F5C] hover:bg-[#35C3C1] hover:text-white transition-colors"
                >
                  CLEAR FILTERS
                </button>
              </div>
            )}

            {/* Pagination (derived from filtered results) */}
            {!loading && totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-1.5 flex-wrap" data-testid="pagination">
                <button onClick={() => setPage(Math.max(1, safePage - 1))} className="h-9 w-9 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors" aria-label="Previous page">
                  <ChevronLeft size={16} />
                </button>
                {pageNums.map((n) => (
                  <button
                    key={n}
                    onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className={`h-9 w-9 rounded-full text-[12.5px] font-extrabold flex items-center justify-center transition-colors ${safePage === n ? "text-white" : "text-gray-500 hover:bg-gray-100"}`}
                    style={safePage === n ? { backgroundColor: NAVY } : {}}
                  >
                    {n}
                  </button>
                ))}
                <button onClick={() => setPage(Math.min(totalPages, safePage + 1))} className="h-9 w-9 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors" aria-label="Next page">
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </section>

        <div className="mt-8">
          <TrustStrip items={config.trustItems} />
        </div>
      </main>

      {/* Mobile filters drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute left-0 top-0 h-full w-[300px] bg-white shadow-2xl overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-extrabold text-lg" style={{ color: NAVY }}>Filters</p>
              <button onClick={() => setMobileFilters(false)} style={{ color: NAVY }} aria-label="Close filters"><X size={22} /></button>
            </div>
            <FilterSidebar config={config.sidebar} filters={filters} setFilters={setFilters} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
          </div>
        </div>
      )}

      {/* Gift Finder Quiz (gifts page only) */}
      {slug === "gifts" && (
        <GiftFinderQuiz
          open={quizOpen}
          onClose={() => setQuizOpen(false)}
          products={products}
          addToCart={addToCart}
          toggleWishlist={toggleWishlist}
          wishlist={wishlist}
        />
      )}

      <Footer />
    </div>
  );
};

export default CategoryPage;
